import type {
  ClubContractSearchResult,
  ClubContractSearchResponse,
  ImpersonateResponse,
} from "@repo/ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiWithLanguage } from "./useApiWithLanguage";
import { useState, useEffect } from "react";
import { useCookies } from "react-cookie";

const impersonateKeys = {
  search: (term: string) => ["impersonate", "search", term] as const,
  state: ["impersonate", "state"] as const,
};

export function useSearchClubContracts(searchTerm: string, enabled: boolean = false) {
  const { fetchWithLanguage } = useApiWithLanguage();

  return useQuery<ClubContractSearchResult[]>({
    queryKey: impersonateKeys.search(searchTerm),
    queryFn: async () => {
      if (!searchTerm) return [];
      
      const response = await fetchWithLanguage(
        `/api/impersonate/search?emailAddressOrCrsReference=${encodeURIComponent(searchTerm)}`,
        {
          credentials: 'include'
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Search failed");
      }
      
      const data: ClubContractSearchResponse = await response.json();
      
      // Transform the nested response to flat array
      return data.backOfficerClubContracts.map(contract => ({
        emailAddress: contract.emailAddress,
        referenceNumber: contract.referenceNumber,
        searchCode: contract.searchCode,
        season: contract.season,
        number: contract.number,
      }));
    },
    enabled: enabled && !!searchTerm,
    staleTime: 0,
    gcTime: 0,
  });
}

interface ImpersonateVariables {
  emailAddress: string;
  season?: number;
  referenceNumber?: string;
}

export function useImpersonate() {
  const { fetchWithLanguage } = useApiWithLanguage();

  return useMutation<ImpersonateResponse, Error, ImpersonateVariables>({
    mutationFn: async ({ emailAddress }) => {
      const response = await fetchWithLanguage("/api/impersonate/execute", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ emailAddress }),
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Impersonation failed");
      }

      const data = await response.json();

      return data.user;
    },
    onSuccess: (_data, variables) => {
      const target = variables.season
        ? `/bookings?year=${variables.season}`
        : '/bookings';
      window.location.href = target;
    },
  });
}

export function useStopImpersonation() {
  const queryClient = useQueryClient();
  const { fetchWithLanguage } = useApiWithLanguage();

  return useMutation({
    mutationFn: async () => {
      const response = await fetchWithLanguage("/api/impersonate/stop", {
        method: "POST",
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error("Failed to stop impersonation");
      }

      return response.json();
    },
    onSuccess: async () => {
      // Clear all queries and refetch
      await queryClient.invalidateQueries();
      
      // Force a hard refresh of the page to ensure all state is reset
      window.location.href = '/bookings';
    },
  });
}

export function useImpersonateState() {
  const [cookies] = useCookies(['isImpersonating', 'user']);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedEmail, setImpersonatedEmail] = useState<string | null>(null);

  useEffect(() => {
    const { isImpersonating: isImpersonatingCookie, user: userCookie } = cookies;

    if (isImpersonatingCookie && userCookie) {
      try {
        const userData = typeof userCookie === 'string' ? JSON.parse(userCookie) : userCookie;
        setIsImpersonating(true);
        setImpersonatedEmail(userData.impersonatedUserName || null);
      } catch (e) {
        console.error('Failed to parse user cookie:', e);
        setIsImpersonating(false);
        setImpersonatedEmail(null);
      }
    } else {
      setIsImpersonating(false);
      setImpersonatedEmail(null);
    }
  }, [cookies]);

  return {
    isImpersonating,
    impersonatedEmail,
  };
}